/* Signal Diagnostics — per-channel operational diagnostics: skip-reason
   breakdown, execution latency, entry slippage, TP1 hit rate, and
   symbol/long-short bias. Real data from
   GET /analytics/channel/{id}/signal_diagnostics, backed by the skip_events
   collection (populated going forward from its 2026-09 deploy — panels here
   start sparse and fill in over time, never presented as if something's
   broken). The "Known Parsing / Execution Issues" list below is a
   hand-curated changelog of real bugs found+fixed on each channel, not a
   live query — labeled as such rather than pretending to be derived data. */
import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { channels, analytics } from "@/lib/api";
import { Badge, cn } from "@/components/atoms";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { MagnifyingGlass } from "@phosphor-icons/react";

const SKIP_REASON_LABELS = {
  skipped_kill_switch: "Kill switch active",
  skipped_max_concurrent: "Max concurrent positions",
  skipped_daily_loss_kill: "Daily loss kill switch",
  skipped_already_open: "Symbol already has an open position",
  skipped_symbol_cooldown: "Symbol still in cooldown",
  skipped_channel_killed: "Channel kill switch active",
  skipped_channel_paused: "Channel paused",
  skipped_no_credentials: "No exchange credentials",
  skipped_exchange_not_implemented: "Exchange routing not implemented",
  skipped_auto_off: "Auto-trading off",
  skipped_incomplete: "Signal incomplete",
  skipped_symbol_not_listed: "Symbol not listed on exchange",
  skipped_position_already_open: "Symbol already has an open position",
  skipped_dont_chase: "Already past TP1 by execution time",
  skipped_no_entry: "No entry price",
  skipped_no_sl: "No stop loss",
  skipped_entry_invalidated_by_sl: "Entry invalidated by SL",
  skipped_sl_beyond_liquidation: "SL beyond liquidation (wide-SL skip)",
  skipped_virtual_balance_zero: "Virtual channel balance <= 0",
  skipped_slippage: "Entry slippage over cap",
  skipped_qty_zero: "Computed size rounded to zero",
};

function humanSkipReason(reason) {
  return SKIP_REASON_LABELS[reason] || (reason || "").replace(/^skipped_/, "").replace(/_/g, " ");
}

/* Hand-curated, matched by a case-insensitive substring of the channel
   name — real project history, not derived from any query. Channels
   without a curated entry just show the "none tracked yet" fallback. */
const KNOWN_ISSUES = [
  {
    match: "gvrcalls",
    issues: [
      { status: "Fixed", title: "Resting limit orders counted as open positions", detail: "pending_limit orders inflated the max_concurrent_positions counter, falsely blocking a real ALCHUSDT signal at 10/10 — fixed 2026-09-02 (500f8d4)." },
      { status: "Fixed", title: "Scraper misread recap/close quote-tweets as fresh entries", detail: "Root cause of a missed JTOUSDT signal — 5 bugs fixed on the twitter-calls droplet, including a self-inflicted kill-switch trip from an earlier dedupe attempt." },
      { status: "Monitoring", title: "RATS-style missed signals — feed visibility, not a bug", detail: "The scraper's top-of-feed-only snapshot can miss a post entirely if enough posts land above it between polls. Ruled out Camofox blindness and dedup/regex bugs — recovered manually when it happens." },
      { status: "Fixed", title: "Chart-title ticker overriding caption-stated symbol", detail: "O1USDT/YEIUSDT chart titles were extracted instead of the caption's real OUSDT/CLOUSDT tickers — fixed via caption-priority rule, 2026-09-02 (405c20b)." },
    ],
  },
  {
    match: "trade talks alpha",
    issues: [
      { status: "Fixed", title: "Chart title symbol overriding caption-stated ticker", detail: "O1USDT/YEIUSDT chart titles were extracted instead of the caption's real O/CLO tickers — fixed via caption-priority rule, deployed 2026-09-02 (405c20b)." },
      { status: "Fixed", title: "Duplicate-ingestion caused real repeat executions", detail: "backfill() had no dup check — confirmed real duplicate fills on Blofin (ZBTUSDT 3x, BTCUSDT 2x) — fixed + 43 duplicate DB records cleaned up." },
      { status: "Monitoring", title: "Idiom words (\"bear with me\") occasionally matching side regex", detail: "A sick-leave message glued a bare \"bear\" match to an unrelated symbol once; caught by skip_if_sl_beyond_liquidation before execution — 3rd instance of this failure class, watching for recurrence." },
      { status: "Monitoring", title: "Wide-SL signals taken as-is: rejected 4x independently", detail: "Every tested variant of \"don't skip wide-SL\" (leverage sweep, margin-mode, dynamic leverage) loses money on this provider — closed, keep skip_if_sl_beyond_liquidation on." },
    ],
  },
  {
    match: "trade sensei",
    issues: [
      { status: "Fixed", title: "realized_pnl overwritten across TP-waterfall legs", detail: "roi_actions.py overwrote instead of accumulated realized_pnl across TP levels — 2 historical trades affected (AVAAIUSDT, BANKUSDT), both corrected against confirmed WEEX data." },
      { status: "Fixed", title: "Single-trade full-balance wipeout path", detail: "Margin-mode + 30x + no SL-distance guard could lose the whole balance in one trade — deployed max_sl_pct=10, caps worst case to 60% of balance at zero backtest cost." },
      { status: "Fixed", title: "Virtual channel balance frozen despite real P&L", detail: "virtual_current_balance stayed at the $150 starting value despite +$28.20 of confirmed real profit — backfilled and reconciled, 2026-08-23." },
    ],
  },
  {
    match: "bitget",
    issues: [
      { status: "Monitoring", title: "Blofin-vs-Bitget candle source mismatch", detail: "Flagged in the first backtest baseline (2026-08-22) — backtests currently price this channel off Blofin candles, not Bitget's own. Unfixed; return figures may be slightly off from live." },
      { status: "Monitoring", title: "Channel too new for a reliable diagnostic baseline", detail: "Conversion, latency and slippage figures above will move as more signal history accumulates." },
    ],
  },
];

function curatedIssuesFor(channelName) {
  const name = (channelName || "").toLowerCase();
  const found = KNOWN_ISSUES.find((k) => name.includes(k.match));
  return found ? found.issues : null;
}

function StatBox({ label, value, note, valueClassName }) {
  return (
    <div className="card-surface p-5">
      <p className="text-xs text-slate-500 font-semibold">{label}</p>
      <p className={cn("mono text-2xl font-bold mt-2", valueClassName)}>{value}</p>
      {note && <p className="text-[11px] text-slate-400 mt-1">{note}</p>}
    </div>
  );
}

function Bar({ pct, colorClass = "bg-[#4F46E5]" }) {
  return (
    <div className="h-2 rounded-full bg-[#4F46E5]/10">
      <div className={cn("h-full rounded-full", colorClass)} style={{ width: `${pct}%` }} />
    </div>
  );
}

export default function SignalDiagnosticsSection() {
  const chQ = useQuery({ queryKey: ["channels"], queryFn: async () => (await channels.list()).data });
  const chList = chQ.data || [];
  const [channelId, setChannelId] = useState("");
  const activeId = channelId || chList[0]?.id || "";
  const ch = chList.find((c) => c.id === activeId);

  const dQ = useQuery({
    queryKey: ["channel-diagnostics", activeId],
    queryFn: async () => (await analytics.channelDiagnostics(activeId)).data,
    enabled: !!activeId,
  });
  const d = dQ.data;

  if (!chQ.isLoading && chList.length === 0) {
    return (
      <div className="card-surface p-6 text-center text-sm text-slate-500">
        No channels configured yet — add one under Channels first.
      </div>
    );
  }

  const skipTotal = d?.total_skips ?? 0;
  const maxSkip = Math.max(1, ...(d?.skip_reasons || []).map((r) => r.count));
  const maxSymbol = Math.max(1, ...(d?.top_symbols || []).map((s) => s.count));
  const longPct = d && (d.long_count + d.short_count) > 0
    ? Math.round((d.long_count / (d.long_count + d.short_count)) * 100) : 50;
  const shortPct = 100 - longPct;
  const curated = curatedIssuesFor(ch?.name);

  return (
    <div className="space-y-4" data-testid="signal-diagnostics-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl">Signal Diagnostics</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Operational diagnostics for a single provider — skip reasons, execution latency, slippage.
            See Channel Analyzer for the historical parser QA report.
          </p>
        </div>
        <Select value={activeId} onValueChange={setChannelId}>
          <SelectTrigger data-testid="diag-channel-picker" className="h-10 w-56">
            <SelectValue placeholder="Pick a channel" />
          </SelectTrigger>
          <SelectContent>
            {chList.map((c) => (
              <SelectItem key={c.id} value={c.id} data-testid={`diag-channel-${c.id}`}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!d ? (
        <div className="card-surface p-6 text-center text-sm text-slate-500">
          {dQ.isLoading ? "Loading…" : "No data yet for this channel."}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatBox
              label="Signal → trade conversion"
              value={d.conversion_pct != null ? `${d.conversion_pct}%` : "—"}
              note={`${d.trades_opened} of ${d.parsed_entry_signals} entry signals executed`}
            />
            <StatBox
              label="Median execution latency"
              value={d.latency_median_seconds != null ? `${d.latency_median_seconds}s` : "—"}
              note={d.latency_sample_n ? `from ${d.latency_sample_n} executed trades` : "from message capture to order placed"}
            />
            <StatBox
              label="Median entry slippage"
              value={d.slippage_median_pct != null ? `${d.slippage_median_pct}%` : "—"}
              note={d.slippage_sample_n
                ? `from ${d.slippage_sample_n} trades since signal_entry_price tracking began`
                : "tracking started 2026-09-04 — no trades yet"}
            />
            <StatBox
              label="Provider TP1 hit rate"
              value={d.tp1_hit_rate_pct != null ? `${d.tp1_hit_rate_pct}%` : "—"}
              valueClassName="text-emerald-600"
              note={d.tp1_sample_n ? `of ${d.tp1_sample_n} executed trades` : "of executed trades reach TP1"}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-4">
            <div className="card-surface p-6">
              <h3 className="font-display text-lg">Why Signals Get Skipped</h3>
              <p className="text-xs text-slate-500 mt-1">
                {skipTotal} skipped signal{skipTotal === 1 ? "" : "s"} recorded since skip-tracking began (2026-09-04)
              </p>
              {(d.skip_reasons || []).length === 0 ? (
                <p className="text-sm text-slate-500 mt-4">
                  No skips recorded yet for this channel — this fills in as signals get processed going forward.
                </p>
              ) : (
                <div className="flex flex-col gap-3.5 mt-5">
                  {d.skip_reasons.map((r) => (
                    <div key={r.reason}>
                      <div className="flex justify-between text-sm mb-1.5">
                        <span className="font-semibold">{humanSkipReason(r.reason)}</span>
                        <span className="mono font-bold">{r.count}</span>
                      </div>
                      <Bar pct={(r.count / maxSkip) * 100} />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="card-surface p-6">
              <h3 className="font-display text-lg">Most-Signaled Symbols</h3>
              <p className="text-xs text-slate-500 mt-1">By parsed entry-signal count, all time</p>
              {(d.top_symbols || []).length === 0 ? (
                <p className="text-sm text-slate-500 mt-4">No entry signals parsed yet for this channel.</p>
              ) : (
                <div className="flex flex-col gap-3 mt-5">
                  {d.top_symbols.map((s) => (
                    <div key={s.symbol} className="flex items-center justify-between gap-3">
                      <span className="mono text-sm font-bold shrink-0 w-24 truncate">{s.symbol}</span>
                      <div className="flex-1"><Bar pct={(s.count / maxSymbol) * 100} /></div>
                      <span className="mono text-xs text-slate-500 shrink-0">{s.count}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="h-px bg-slate-100 my-5" />
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 font-semibold">Long vs. short bias</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    {d.long_count + d.short_count === 0
                      ? "no entry signals yet"
                      : `${d.long_count} long · ${d.short_count} short`}
                  </p>
                </div>
                <div className="flex gap-0.5 w-36 h-2.5 rounded-full overflow-hidden">
                  <div className="bg-emerald-500" style={{ width: `${longPct}%` }} />
                  <div className="bg-red-500" style={{ width: `${shortPct}%` }} />
                </div>
              </div>
            </div>
          </div>

          <div className="card-surface p-6">
            <h3 className="font-display text-lg">Known Parsing / Execution Issues</h3>
            <p className="text-xs text-slate-500 mt-1">
              Hand-maintained changelog of bugs found via this channel's signal history — not a live query.
            </p>
            {!curated ? (
              <p className="text-sm text-slate-500 mt-4 flex items-center gap-2">
                <MagnifyingGlass size={14} /> No issues tracked yet for this channel.
              </p>
            ) : (
              <div className="flex flex-col mt-3">
                {curated.map((iss, i) => (
                  <div key={i} className="flex items-start gap-3 py-3 border-t border-slate-100 first:border-t-0 first:pt-0">
                    <Badge variant={iss.status === "Fixed" ? "profit" : "info"} className="mt-0.5 shrink-0">
                      {iss.status}
                    </Badge>
                    <div>
                      <p className="text-sm font-bold">{iss.title}</p>
                      <p className="text-xs text-slate-500 mt-1 leading-relaxed">{iss.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
